import React, { useState } from 'react';
import { Currency, formatMoney } from '../../common/format';
import { RequestResponseViewer } from '../../common/request-response-viewer';
import {
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
  buildMonthlyProjectionColumns,
  buildProjectionColumns,
} from '../_shared';
import {
  InsuredUniversityResponse,
  UniversityMonthlyPolicyRow,
  UniversityPolicyRow,
} from './service';

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'accumulated_prime', name: 'Prima acumulada', stroke: 'var(--bs-gray-600)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemnización fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
  {
    dataKey: 'withdrawal',
    name: 'Retiro universitario',
    stroke: 'var(--bs-warning)',
  },
];

const WITHDRAWAL_COLUMNS: ProjectionColumn<UniversityPolicyRow>[] = [
  { id: 'withdrawal', label: 'Retiro', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
];

const UNIVERSITY_COLUMNS = buildProjectionColumns<UniversityPolicyRow>(WITHDRAWAL_COLUMNS);

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'withdrawal', name: 'Retiro', stroke: 'var(--bs-warning)' },
];

const UNIVERSITY_MONTHLY_COLUMNS = buildMonthlyProjectionColumns<UniversityMonthlyPolicyRow>([
  { id: 'withdrawal', label: 'Retiro', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
]);

interface InsuredUniversityResultViewProps {
  request: unknown;
  response?: InsuredUniversityResponse;
  error?: unknown;
}

export function InsuredUniversityResultView({
  request,
  response,
  error,
}: InsuredUniversityResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-primary"
              />
              <KpiCard
                label="Ahorro neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
                accent="text-success"
              />
              <KpiCard
                label="Ganado por interés"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima acordada"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
              <KpiCard
                label="Cobertura del ideal"
                value={`${(response.coverage_factor * 100).toFixed(1)}%`}
                hint="Arancel financiado sobre el arancel ideal"
                accent={response.coverage_factor < 1 ? 'text-warning' : 'text-success'}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                [
                  'policy_value',
                  'accumulated_prime',
                  'death_compensation',
                  'withdrawal',
                ],
                currency,
                response.uf_value,
              )}
              series={CHART_SERIES}
              currency={currency}
            />
          </div>

          {response.projection_by_month && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    response.projection_by_month,
                    ['policy_value', 'withdrawal'],
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={MONTHLY_CHART_SERIES}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={response.projection_by_month}
                  columns={UNIVERSITY_MONTHLY_COLUMNS}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={UNIVERSITY_COLUMNS}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
